import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Helmet } from "react-helmet";
import Navbar from "../../_components/Navbar/Navbar";
import Footer from "../../_components/Footer";
import { AlphabetNavigation } from "./components/AlphabetNavigation";
import { TermsList } from "./components/TermsList";
import { PromotionalText } from "./components/promotional-text";
import {
  getAvailableLetters,
  getTermsForLetter,
  glossaryData,
} from "./constants";
import { allowedArtisans } from "../hire-a-pro/constants";
import { FaArrowLeft } from "react-icons/fa";
import { PageSection } from "_components/PageSection";
import { useCategories } from "hooks/useCategories";

interface GlossaryParams {
  category: string;
  letter: string;
}

export const GlossaryByLetter: React.FC = () => {
  const { category, letter = "a" } = useParams<GlossaryParams>();

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategories();

  const categoryId = categories?.find(
    (c) =>
      c.name.toLowerCase() === category.toLowerCase() ||
      c.name.toLowerCase() === category.toLowerCase().replace(/-/g, " ") ||
      c.name.toLowerCase() === category.toLowerCase().replace(/-/g, "")
  )?.id;

  if (!category) {
    return <div>Invalid category</div>;
  }

  // Check if artisan exists in glossary data
  if (!glossaryData[category]) {
    return <div>Category glossary not found</div>;
  }

  const availableLetters = getAvailableLetters(category);
  const terms = getTermsForLetter(category, letter);
  const currentLetter = letter.toUpperCase();

  // Get formatted artisan name
  const categoryData = allowedArtisans[category];
  const formattedCategoryName =
    categoryData?.formattedName ||
    category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, " ");

  return (
    <>
      <Helmet>
        <title>
          {`${formattedCategoryName} Glossary - ${currentLetter} | LaborHack`}
        </title>
        <meta
          name="description"
          content={`Browse ${formattedCategoryName
            .replace(/-/g, " ")
            .toLowerCase()} terms starting with ${currentLetter}. Professional definitions and explanations for industry-specific terminology.`}
        />
      </Helmet>

      <Navbar colorScheme="prussianBlue" />

      <div className="bg-[#FEFEFE] min-h-screen py-10">
        <PageSection>
          {/* Breadcrumb */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <RouterLink to="/glossary">Glossary</RouterLink>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <RouterLink to={`/glossary/${category}/a`}>
                    {formattedCategoryName}
                  </RouterLink>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{currentLetter}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-[#153243] text-2xl md:text-5xl font-medium mb-2 font-[AeonikPro]">
              {formattedCategoryName} glossary
            </h1>
            <p className="text-[#646D72] text-base md:text-lg font-[Inter]">
              Professional terms and definitions for{" "}
              {formattedCategoryName.toLowerCase()} work
            </p>
          </div>

          {/* Alphabet Navigation */}
          <AlphabetNavigation
            availableLetters={availableLetters}
            currentLetter={currentLetter}
            category={category}
          />

          {!categoriesError && (
            <PromotionalText
              text={glossaryData[category].promotionalText}
              category={categoryId}
              loading={categoriesLoading}
            />
          )}

          {/* Terms List */}
          <TermsList terms={terms} letter={currentLetter} category={category} />

          {/* Back to glossary link */}
          <div className="text-center pt-6">
            <RouterLink
              to="/glossary"
              className="w-fit text-[#ED6E5C] hover:text-[#D85D4A] text-sm md:text-base font-medium hover:underline flex items-center gap-2"
            >
              <FaArrowLeft className="w-3 h-3 md:w-3.5 md:h-3.5" /> Back to all
              glossaries
            </RouterLink>
          </div>
        </PageSection>
      </div>

      <Footer />
    </>
  );
};
